import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, streamChat, API } from "@/lib/api";
import { Send, Plus, MessageSquare, Flame, Trash2, Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STARTERS = [
  "How do I approach a woman at a coffee shop without being awkward?",
  "She stopped replying after 2 days. Should I text again?",
  "What should I text her the day after a first date?",
  "How do I keep things exciting in a 2-year relationship?",
];

const Coach = () => {
  const [params, setParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [currentId, setCurrentId] = useState(params.get("s") || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [assistantBuf, setAssistantBuf] = useState("");
  const scrollRef = useRef(null);

  // Voice recording state
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        streamRef.current?.getTracks().forEach((t) => t.stop());
        setTranscribing(true);
        try {
          const form = new FormData();
          form.append("audio", blob, "voice.webm");
          const token = localStorage.getItem("suitor_token");
          const res = await fetch(`${API}/transcribe`, {
            method: "POST",
            credentials: "include",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: form,
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || "transcribe failed");
          const text = (data.text || "").trim();
          if (text) {
            setInput((p) => (p ? (p + " " + text) : text));
            toast.success("Transcribed");
          } else {
            toast("No speech detected");
          }
        } catch (err) {
          toast.error("Voice transcription failed");
        }
        setTranscribing(false);
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch (err) {
      toast.error("Microphone permission denied");
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    setRecording(false);
  };

  const loadSessions = async () => {
    const r = await api.get("/chat/sessions");
    setSessions(r.data || []);
  };

  useEffect(() => { loadSessions(); }, []);

  useEffect(() => {
    if (currentId) {
      api.get(`/chat/sessions/${currentId}/messages`).then((r) => setMessages(r.data || []));
    } else {
      setMessages([]);
    }
  }, [currentId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, assistantBuf]);

  const newSession = async () => {
    const r = await api.post("/chat/sessions", { title: "New Conversation" });
    await loadSessions();
    setCurrentId(r.data.session_id);
    setParams({ s: r.data.session_id });
    setMessages([]);
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    let sid = currentId;
    if (!sid) {
      const r = await api.post("/chat/sessions", { title: text.slice(0, 60) });
      sid = r.data.session_id;
      setCurrentId(sid);
      setParams({ s: sid });
      await loadSessions();
    }
    const userMsg = { message_id: `local_${Date.now()}`, role: "user", text, created_at: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    setStreaming(true);
    setAssistantBuf("");

    await streamChat(
      sid,
      text,
      (delta) => setAssistantBuf((b) => b + delta),
      async () => {
        // Reload messages for the final persisted content
        const r = await api.get(`/chat/sessions/${sid}/messages`);
        setMessages(r.data || []);
        setAssistantBuf("");
        setStreaming(false);
        loadSessions();
      },
      (err) => {
        toast.error("Stream error: " + (err.message || "unknown"));
        setStreaming(false);
      }
    );
  };

  const removeSession = async (id) => {
    await api.delete(`/chat/sessions/${id}`);
    if (currentId === id) { setCurrentId(null); setParams({}); }
    loadSessions();
  };

  return (
    <div className="h-screen md:h-auto md:min-h-[calc(100vh-0px)] flex">
      {/* Session list */}
      <aside className="hidden lg:flex flex-col w-72 border-r border-white/5 bg-[#0c0c0e] p-4">
        <button onClick={newSession} className="btn-amber flex items-center justify-center gap-2 mb-4" data-testid="new-chat-btn">
          <Plus className="w-4 h-4" /> New conversation
        </button>
        <div className="flex-1 overflow-y-auto space-y-1">
          {sessions.length === 0 && <div className="text-sm text-neutral-500 px-2 py-4" data-testid="no-chats">Start your first chat.</div>}
          {sessions.map((s) => (
            <div
              key={s.session_id}
              className={`group flex items-center justify-between rounded-lg px-3 py-2 text-sm cursor-pointer ${
                currentId === s.session_id ? "bg-amber-500/10 text-amber-400" : "text-neutral-400 hover:bg-white/5"
              }`}
              onClick={() => { setCurrentId(s.session_id); setParams({ s: s.session_id }); }}
              data-testid={`session-item-${s.session_id}`}
            >
              <div className="truncate flex-1">{s.title}</div>
              <button onClick={(e) => { e.stopPropagation(); removeSession(s.session_id); }} className="opacity-0 group-hover:opacity-100 text-neutral-500 hover:text-red-400">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </aside>

      {/* Chat area */}
      <div className="flex-1 flex flex-col">
        <div className="px-6 py-6 border-b border-white/5 flex items-center justify-between">
          <div>
            <div className="overline text-neutral-500">AI COACH</div>
            <h1 className="font-serif text-3xl" data-testid="coach-title">Ask Suitor</h1>
          </div>
          <button onClick={newSession} className="lg:hidden btn-ghost text-sm flex items-center gap-2" data-testid="mobile-new-chat-btn">
            <Plus className="w-4 h-4"/> New
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {messages.length === 0 && !assistantBuf && (
            <div className="max-w-2xl mx-auto text-center py-10">
              <Flame className="w-10 h-10 text-amber-500 mx-auto mb-4"/>
              <h2 className="font-serif text-3xl mb-3">What's on your mind?</h2>
              <p className="text-neutral-400 mb-6">Ask about approach, texting, ghosting, first dates, or long-term love.</p>
              <div className="grid sm:grid-cols-2 gap-3 text-left">
                {STARTERS.map((s, i) => (
                  <button key={i} onClick={() => send(s)} className="card-surface p-4 text-sm text-neutral-300 hover-lift" data-testid={`starter-${i}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => (
            <MessageBubble key={m.message_id} role={m.role} text={m.text} />
          ))}
          {assistantBuf && <MessageBubble role="assistant" text={assistantBuf} streaming />}
          {streaming && !assistantBuf && (
            <div className="flex items-center gap-2 text-neutral-500">
              <div className="typing-dot"/><div className="typing-dot"/><div className="typing-dot"/>
            </div>
          )}
        </div>

        <div className="border-t border-white/5 p-4">
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="max-w-3xl mx-auto flex items-end gap-3"
          >
            <textarea
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={recording ? "Recording... release to transcribe" : "Ask Suitor anything..."}
              disabled={recording || transcribing}
              className="flex-1 resize-none min-h-[52px] max-h-[160px] px-4 py-3 bg-[#141416] border border-white/10 rounded-2xl text-white focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/40 disabled:opacity-70"
              data-testid="chat-input"
            />
            <button
              type="button"
              onMouseDown={startRecording}
              onMouseUp={stopRecording}
              onMouseLeave={() => recording && stopRecording()}
              onTouchStart={(e) => { e.preventDefault(); startRecording(); }}
              onTouchEnd={(e) => { e.preventDefault(); stopRecording(); }}
              disabled={streaming || transcribing}
              className={`h-[52px] px-4 rounded-full border transition-colors inline-flex items-center justify-center ${
                recording
                  ? "bg-red-500/90 border-red-400 text-white animate-pulse"
                  : "border-white/10 text-neutral-300 hover:text-white hover:border-amber-500/40"
              } disabled:opacity-50`}
              title={recording ? "Release to transcribe" : "Hold to speak"}
              data-testid="voice-mic-btn"
            >
              {transcribing ? <Loader2 className="w-5 h-5 animate-spin" /> : recording ? <Square className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>
            <button
              type="submit"
              disabled={streaming || !input.trim() || recording || transcribing}
              className="btn-amber h-[52px] px-5 disabled:opacity-50"
              data-testid="chat-send-btn"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
          <p className="text-center text-[11px] text-neutral-600 mt-2">
            {recording ? "Release the mic to transcribe." : transcribing ? "Transcribing…" : "Press & hold the mic to speak."}
          </p>
        </div>
      </div>
    </div>
  );
};

const MessageBubble = ({ role, text, streaming }) => {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`msg-${role}`}>
      <div className={`max-w-2xl ${isUser ? "" : "border-l-2 border-amber-500 pl-4"}`}>
        {!isUser && <div className="overline mb-1 text-neutral-500">SUITOR</div>}
        <div
          className={
            isUser
              ? "bg-[#1c1c1f] border border-white/10 rounded-2xl px-4 py-3 text-neutral-100 whitespace-pre-wrap"
              : "text-neutral-200 whitespace-pre-wrap leading-relaxed"
          }
        >
          {text}
          {streaming && <span className="inline-block w-2 h-4 bg-amber-500 ml-1 align-middle animate-pulse"/>}
        </div>
      </div>
    </div>
  );
};

export default Coach;
